import { useState } from 'react';
import type { PlanStep, StepResult } from '@verifyloop/core';
import type { TestSummary } from '../api/types';
import { getRun, rerunTest } from '../api/endpoints';
import { describeError } from '../api/client';
import { useResource, useToast } from '../state';
import { formatDuration, formatRelative } from '../lib/format';
import { Modal } from './Form';
import { LoadingRows, ErrorNotice } from './Feedback';
import { ArtifactPreview } from './Artifact';
import { Mono, RunStatusPill, StepStatusPill, StepTypePill } from './Pills';
import { PlaybookFlow } from './PlaybookFlow';
import { PlanStepsView } from './StepViews';

export interface PlaybookInspectModalProps {
  test: TestSummary;
  onClose: () => void;
  onEditPlan?: () => void;
  onTestUpdated?: () => void;
}

export function PlaybookInspectModal({
  test,
  onClose,
  onEditPlan,
  onTestUpdated,
}: PlaybookInspectModalProps): JSX.Element {
  const toast = useToast();
  const [selectedStepIndex, setSelectedStepIndex] = useState<number>(0);
  const [viewMode, setViewMode] = useState<'flow' | 'list'>('flow');
  const [running, setRunning] = useState(false);

  // Load the latest run data if available to populate step results and forensic artifacts
  const latestRunId = test.latestRun?.id;
  const runResource = useResource(
    () => (latestRunId ? getRun(latestRunId) : Promise.resolve(null)),
    [latestRunId],
    { live: ['run.updated', 'run.finished'] },
  );

  const runData = runResource.data?.run;
  const stepResults: readonly StepResult[] | undefined = runData?.steps;

  const currentStep: PlanStep | undefined = test.planSteps[selectedStepIndex];
  const currentResult: StepResult | undefined = stepResults?.[selectedStepIndex];

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const accepted = await rerunTest(test.id, { wait: true, timeoutMs: 300_000 });
      if (accepted.runIds.length === 0) {
        toast.push({
          kind: 'error',
          message: `“${test.name}” did not start`,
          detail: accepted.skipped[0]?.reason ?? 'the run queue refused it',
        });
        return;
      }
      toast.success(`“${test.name}” executed`, `Run ${accepted.runIds[0]} completed`);
      runResource.reload();
      onTestUpdated?.();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Modal title={`Playbook: ${test.name}`} onClose={onClose} wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Top Action & Metadata Bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
            borderBottom: '1px solid var(--border)',
            paddingBottom: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className={`pill pill--${test.type === 'frontend' ? 'info' : 'neutral'} pill--subtle`}>
              {test.type}
            </span>
            {test.latestRun ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <span className="text-muted">Latest Run:</span>
                <RunStatusPill status={test.latestRun.status} />
                <span className="text-muted">({formatRelative(test.latestRun.startedAt)})</span>
              </span>
            ) : (
              <span className="text-muted" style={{ fontSize: 13 }}>
                Never run yet
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                display: 'inline-flex',
                borderRadius: 6,
                border: '1px solid var(--border)',
                overflow: 'hidden',
              }}
            >
              <button
                type="button"
                className={`btn btn--small ${viewMode === 'flow' ? 'btn--secondary' : 'btn--ghost'}`}
                onClick={() => setViewMode('flow')}
                style={{ borderRadius: 0 }}
              >
                📊 Flow Diagram
              </button>
              <button
                type="button"
                className={`btn btn--small ${viewMode === 'list' ? 'btn--secondary' : 'btn--ghost'}`}
                onClick={() => setViewMode('list')}
                style={{ borderRadius: 0 }}
              >
                📋 Classic List
              </button>
            </div>

            <button
              type="button"
              className="btn btn--small"
              disabled={running}
              onClick={() => void handleRunNow()}
            >
              {running ? 'Running…' : '▶ Run Test'}
            </button>

            {onEditPlan ? (
              <button
                type="button"
                className="btn btn--small btn--ghost"
                onClick={() => {
                  onClose();
                  onEditPlan();
                }}
              >
                Edit Plan
              </button>
            ) : null}
          </div>
        </div>

        {test.description ? <p className="muted" style={{ margin: 0 }}>{test.description}</p> : null}

        {/* View Content: Flow Graph or Classic List */}
        {viewMode === 'list' ? (
          <PlanStepsView steps={test.planSteps} />
        ) : (
          <div
            style={{
              background: 'var(--bg-inset)',
              borderRadius: 8,
              border: '1px solid var(--border)',
              padding: 12,
            }}
          >
            <PlaybookFlow
              steps={test.planSteps}
              stepResults={stepResults}
              selectedStepIndex={selectedStepIndex}
              onSelectStep={(idx) => setSelectedStepIndex(idx)}
              activeRunningIndex={running ? 0 : null}
              showSwitcher={true}
            />
          </div>
        )}

        {/* In-Context Step Record Panel */}
        {currentStep ? (
          <div
            className="panel"
            style={{
              marginTop: 4,
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 16,
              background: 'var(--bg-panel)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 12,
                borderBottom: '1px solid var(--border)',
                paddingBottom: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>
                  STEP #{selectedStepIndex + 1} RECORD
                </span>
                <StepTypePill type={currentStep.type} />
                {currentResult ? <StepStatusPill status={currentResult.status} /> : null}
              </div>

              {currentResult?.durationMs !== undefined ? (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Duration: {formatDuration(currentResult.durationMs)}
                </span>
              ) : null}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <strong style={{ fontSize: 13, color: 'var(--foreground)' }}>Description: </strong>
                <span style={{ fontSize: 13 }}>{currentStep.description}</span>
              </div>

              {currentStep.target ? (
                <div>
                  <strong style={{ fontSize: 12, color: 'var(--text-muted)' }}>Target: </strong>
                  <Mono>{currentStep.target}</Mono>
                </div>
              ) : null}

              {currentResult?.resolvedTarget ? (
                <div>
                  <strong style={{ fontSize: 12, color: 'var(--text-muted)' }}>Resolved Target: </strong>
                  <Mono>{currentResult.resolvedTarget}</Mono>
                </div>
              ) : null}

              {currentResult?.error ? (
                <div
                  style={{
                    marginTop: 8,
                    padding: 10,
                    borderRadius: 6,
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid var(--fail, #ef4444)',
                    color: 'var(--fail, #ef4444)',
                    fontSize: 12,
                    fontFamily: 'monospace',
                  }}
                >
                  <strong>Execution Error:</strong>
                  <pre style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{currentResult.error}</pre>
                </div>
              ) : null}

              {/* Step Artifacts: Screenshot, Video, DOM Snapshot */}
              {runData && currentResult?.artifacts && currentResult.artifacts.length > 0 ? (
                <div style={{ marginTop: 12 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: 'var(--text-muted)',
                      marginBottom: 8,
                    }}
                  >
                    Recorded Step Artifacts ({currentResult.artifacts.length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {currentResult.artifacts.map((artifact) => (
                      <div
                        key={artifact.id}
                        style={{
                          border: '1px solid var(--border)',
                          borderRadius: 6,
                          padding: 8,
                          background: 'rgba(0,0,0,0.2)',
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--text-muted)',
                            marginBottom: 6,
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <span style={{ textTransform: 'uppercase', fontWeight: 600 }}>{artifact.kind}</span>
                          <Mono>{artifact.id}</Mono>
                        </div>
                        <ArtifactPreview runId={runData.id} artifact={artifact} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : runData && currentResult?.status === 'passed' ? (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 12,
                    color: 'var(--text-muted)',
                    fontStyle: 'italic',
                    background: 'rgba(255,255,255,0.02)',
                    padding: 8,
                    borderRadius: 4,
                  }}
                >
                  ✓ Step passed cleanly. Playwright captures screenshots on step failure or on full video flush.
                </div>
              ) : !runData ? (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 12,
                    color: 'var(--text-muted)',
                    fontStyle: 'italic',
                  }}
                >
                  No run history available for this test yet. Click "Run Test" above to execute and capture step records.
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {runResource.loading ? <LoadingRows rows={2} label="Refreshing run data…" /> : null}
        {runResource.error ? <ErrorNotice error={runResource.error} /> : null}
      </div>
    </Modal>
  );
}
